/**
 * Single source of truth for discriminating the tool-result envelope into
 * success vs. failure for downstream consumers.
 *
 * Tool `execute()` results are either a success body or a structured
 * failure payload (see `ToolOutputErrorSchema`:
 * `{ success: false, errorType, error, suggestions?, details?, recovery? }`).
 * The LLM transcript render (`Transcript.tsx`), the in-UI "Show
 * details" view (`ToolResultDetails`) and the rerun debug hook all
 * discriminate the two through this helper so the sites cannot drift.
 *
 * On failure the parsed structured envelope is returned as `error` (for
 * the render pipeline's per-tool `message.error` formatter), and its
 * message is also surfaced flat as `errorMessage` for consumers that
 * only need a string.
 */

import { UnexpectedError } from './errors/index.ts';
import { isToolErrorOutput, type ToolOutputError, ToolOutputErrorSchema } from './output-types.ts';

export type ResultEnvelope = { kind: 'success' } | { kind: 'failure'; errorMessage: string; error: ToolOutputError };

const FALLBACK_ERROR_MESSAGE = 'Tool call failed.';

/**
 * Inspect a tool-result `output` value and report whether it carries the
 * failure marker (`success: false`). Anything else — including missing
 * fields, plain objects, primitives — is treated as a success and the
 * caller is expected to read the value with the tool's own formatter.
 *
 * A failure that doesn't satisfy {@link ToolOutputErrorSchema} (malformed
 * payload) is normalised to a synthetic {@link UnexpectedError} envelope so
 * every failure consumer gets a well-formed {@link ToolOutputError}. The
 * discriminator is sourced from the `@toolError` registry rather than minted
 * as a bare literal, so it stays visible to `humanizeErrorType` and the
 * discriminator test.
 */
export function parseResultEnvelope(value: unknown): ResultEnvelope {
  if (!isToolErrorOutput(value)) {
    return { kind: 'success' };
  }
  const parsed = ToolOutputErrorSchema.safeParse(value);
  const error: ToolOutputError = parsed.success
    ? parsed.data
    : { success: false, errorType: UnexpectedError.errorType, error: FALLBACK_ERROR_MESSAGE };
  return { kind: 'failure', errorMessage: error.error, error };
}
/**
 * Whether a settled tool-result `output`/`result` value carries the failure
 * marker. A `undefined` result (in-progress / no result yet) is never a
 * failure. The single discrimination point shared by `usePartStatus`,
 * `isErroredToolPart`, `tool-identity` and `bindingProjection`, so the
 * boolean classification cannot drift from {@link parseResultEnvelope}.
 */
export function isFailureResult(result: unknown): boolean {
  return result !== undefined && parseResultEnvelope(result).kind === 'failure';
}

/**
 * Whether a tool-call part is in a result-level failure state: either the
 * AI SDK protocol-error flag (`isError === true`) is set, or its settled
 * result envelope is a structured {@link isFailureResult}. Wraps
 * {@link isFailureResult} with the `isError` arm for the consumers that fold
 * both channels together (`tool-identity`, `bindingProjection`).
 */
export function isFailurePart(part: { isError?: boolean; result?: unknown }): boolean {
  return part.isError === true || isFailureResult(part.result);
}
